import type { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
// eslint-disable-next-line no-restricted-imports
import { MatDialog } from '@angular/material/dialog';
import type { MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import type { Observable } from 'rxjs';
import type { IfAny } from 'type-fest';

interface TypedDialogComponent<T = unknown, D = unknown, R = unknown> {
  data: D;
  dialogRef: MatDialogRef<T, R>;
}

type NoAny<T> = IfAny<T, void, T>;

type ExtractDialogParams<T> =
  T extends TypedDialogComponent<
    infer ComponentType,
    infer DataType,
    infer ResultType
  >
    ? { component: ComponentType; data: DataType; result: ResultType }
    : T extends Omit<
          TypedDialogComponent<infer ComponentType, unknown, infer ResultType>,
          'data'
        >
      ? { component: ComponentType; data: void; result: ResultType }
      : T extends Omit<
            TypedDialogComponent<unknown, infer DataType, unknown>,
            'dialogRef'
          >
        ? { component: unknown; data: DataType; result: void }
        : { component: unknown; data: void; result: void };

type DataOfDialog<T> = NoAny<ExtractDialogParams<T>['data']>;
type ResultOfDialog<T> = NoAny<ExtractDialogParams<T>['result']>;

export type TypedDialogRef<T> = MatDialogRef<T, ResultOfDialog<T>>;

export interface MatDialogRefStrict<T, R> extends MatDialogRef<T, R> {
  afterClosed(): Observable<R>;
  beforeClosed(): Observable<R>;
  close(dialogResult: R): void;
}

export type TypedDialogRefDisableClose<T> = MatDialogRefStrict<
  T,
  ResultOfDialog<T>
>;

@Injectable()
export class TypedDialogService {
  constructor(protected readonly matDialog: MatDialog) {}

  getDialogById(id: string) {
    return this.matDialog.getDialogById(id);
  }

  open<T extends { data?: void; dialogRef?: T['dialogRef'] }>(
    component: ComponentType<T>,
    data: MatDialogConfig<DataOfDialog<T>> & { disableClose: true },
  ): TypedDialogRefDisableClose<T>;
  open<T>(
    component: ComponentType<T>,
    data: MatDialogConfig<DataOfDialog<T>> & {
      data: DataOfDialog<T>;
      disableClose: true;
    },
  ): TypedDialogRefDisableClose<T>;
  open<T extends { data?: void; dialogRef?: T['dialogRef'] }>(
    component: ComponentType<T>,
    data?: MatDialogConfig<DataOfDialog<T>>,
  ): TypedDialogRef<T>;
  open<T>(
    component: ComponentType<T>,
    data: MatDialogConfig<DataOfDialog<T>> & {
      data: DataOfDialog<T>;
    },
  ): TypedDialogRef<T>;
  open<T>(
    component: ComponentType<T>,
    data?: MatDialogConfig<DataOfDialog<T>>,
  ): TypedDialogRef<T> {
    return this.matDialog.open<T, DataOfDialog<T>, ResultOfDialog<T>>(
      component,
      data,
    );
  }
}
